import { ColDef, PostSortRowsParams, RowClickedEvent } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { aggridHeaderClass, titleMRClass } from "src/components/common/common";
import useUiuxStore from "src/store/useUiuxStore";
import { CreateCellClickHandler, createCellCssClassRules, CreateCellDoubleClickHandler, CustomCellClickAction, CustomCellDoubleClickAction, GridCellConfig, InsertRowHandler, postSortHandler, RemoveRowHandler, useCellChangeHandler, useRowSelectionHandler } from "src/utils/grid/GridHandlerContent";
import { useGridSettingContent } from "src/utils/grid/GridSettingContent";
import { defaultColDef, getDefaultRowIndexColumnConfig, getRowStyleFlags, IndexParameterCell } from "src/utils/grid/gridStyleUtils";
import { useWMS1030_R01Store } from "./Store_WMS1030_R01";

import { GridContextWrapper } from "src/components/common/gridWrapper/GridContextWrapper";
import { GridCustomColumnDefs } from "src/utils/grid/GridCustomColumnDefs";
import { buildDefaultColumnStateFromDefs } from "src/utils/grid/columnPinUtils";
import { WMS1030_R01LotDto } from "./Dto_WMS1030_R01";

import Grid_Excel from "src/components/common/gridWrapper/Grid_Excel";
import { useDetailTableHandlers as useCommonDetailTableHandlers } from "src/hooks/useDetailTableHandlers";

// 범용 훅 임포트
import { useDetailTableEvents } from "src/hooks/detail-table/useDetailTableEvents";
import { useCommonCodeStore } from "src/store/useCommonCodeStore";

const COLUMN_STATE_KEY = 'WMS1030_R01-lot-grid-column-state';

interface Props {
	lotGridRef: React.RefObject<AgGridReact<WMS1030_R01LotDto> | null>;
	defaultWMS1030_R01LotRow: WMS1030_R01LotDto;
	clickable: boolean;
	setClickable: (clickable: boolean) => void;
}

export const LotTable_WMS1030_R01 = (props: Props) => {

	const { lotGridRef, defaultWMS1030_R01LotRow, clickable, setClickable } = props;

	const {
		WMS1030_R01LotMap,
		setWMS1030_R01LotList,
		updateWMS1030_R01LotList,
		selectedWMS1030_R01LotList, addSelectedWMS1030_R01LotList, removeSelectedWMS1030_R01LotList,
		initSelectedWMS1030_R01LotList,
		deletedWMS1030_R01LotList,
		originalWMS1030_R01LotList,
		selectedItemCdForLot,
		lotColumnState, setLotColumnState,
		lotScrollTop, setLotScrollTop,
		editWMS1030_R01,
	} = useWMS1030_R01Store();

	const gridWrapperRef = useRef<HTMLDivElement>(null);

	const gridData = useMemo(
		() => WMS1030_R01LotMap.get(selectedItemCdForLot || '') || [],
		[WMS1030_R01LotMap, selectedItemCdForLot],
	);

	const {
		getRowId,
		createRowClassRules,
		createCellClassRules,
		paddedGridData,
		renderIndexCell,
	} = useGridSettingContent({
		gridRef: lotGridRef,
		key: 'valueIndex',
		gridData,
		defaultGridData: defaultWMS1030_R01LotRow,
		selectedRow: selectedWMS1030_R01LotList,
		originalGridData: originalWMS1030_R01LotList,
		updateRow: (index: number, data: any) => updateWMS1030_R01LotList(index, data),
	});

	const headerNames: Record<string, string> = {
		rowIndex: '',
		lotNo: 'LOT No',
		lotQty: 'LOT수량',
		inputOutQty: '출고량',
	};

	const cssCellConfig = useMemo<GridCellConfig>(() => ({
		disabledCells: ['lotNo', 'lotQty'],
		insertedDisabledCells: [''],
		commonDisabledCells: [''],
		
		pointerCells: ['inputOutQty'],
		insertedPointerCells: [''],
		commonPointerCells: [''],
		
		alignLeftCells: ['']
	}), []);

	const {
		disabledInsertedColumns, disabledColumns,
		pointerInsertedColumns, pointerColumns,
		alignLeftColumns,
	} = useMemo(() => createCellCssClassRules(cssCellConfig), [cssCellConfig]);
	
	const cellCssDefaultColDef = useMemo(() => {
        const baseRules = createCellClassRules<WMS1030_R01LotDto>({
            idKey: 'valueIndex',
            selectedList: selectedWMS1030_R01LotList,
            deletedList: deletedWMS1030_R01LotList,
            disabledInsertedColumns,	disabledColumns,
            pointerInsertedColumns,		pointerColumns,
            alignLeftColumns,
        });

        return { ...defaultColDef, cellClassRules: { ...baseRules, }}; 
	}, [
		
        selectedWMS1030_R01LotList, deletedWMS1030_R01LotList,
        disabledInsertedColumns, disabledColumns,         
        pointerInsertedColumns, pointerColumns,
		alignLeftColumns, createCellClassRules,
	]);

	const commonInputColumns = ['lotNo', 'lotQty', 'inputOutQty'];
	// 범용 이벤트 훅을 위한 설정
	const defaultEditableColumns = ['lotNo', 'lotQty', 'inputOutQty'];

	const baseColumnDefs: ColDef<WMS1030_R01LotDto>[] = useMemo(() => [
		...getDefaultRowIndexColumnConfig(),
		{
			headerName: '재고량', colId: 'lotQty', field: 'lotQty', width: 65,
			editable: false,
			comparator: (valueA, valueB) => Number(valueA) - Number(valueB),
			valueFormatter: (params) => params.value?.toLocaleString() ?? ''
		},
		{
			headerName: '출고량', colId: 'inputOutQty', field: 'inputOutQty', width: 65,
			editable: true,
			comparator: (valueA, valueB) => Number(valueA) - Number(valueB),
			valueFormatter: (params) => params.value?.toLocaleString() ?? ''
		},
        {
            headerName: '상태', colId: 'itemStatus', field: 'itemStatus', width: 50,
			editable: false,
		},
        {
            headerName: 'LOT No', colId: 'lotNo', field: 'lotNo', width: 130,
			editable: false,
		},
		// ...IndexParameterCell(),
	], [
		deletedWMS1030_R01LotList, gridData, createCellClassRules,
	]);

	
	const defaultColumnState = useMemo(
		() => buildDefaultColumnStateFromDefs(baseColumnDefs, []),
	[baseColumnDefs]);

	const columnDefs: ColDef<WMS1030_R01LotDto>[] = useMemo(() => {
		if (lotColumnState === null) {
			return baseColumnDefs;
		}
		
		return GridCustomColumnDefs<WMS1030_R01LotDto>({
			baseColumnDefs,
			columnState: lotColumnState,
			headerNames,
			fallbackHeaderClass: aggridHeaderClass
		});
	}, [baseColumnDefs, lotColumnState, headerNames]);

	
	const handleCellChange = useCellChangeHandler({
		dataList: gridData,
		originalDataList: originalWMS1030_R01LotList,
		updateRow: updateWMS1030_R01LotList,
		customBeforeUpdate: (updatedData, field, value) => {
			return updatedData;
		},
		customAfterUpdate: (updatedData, field, value) => {
			return updatedData;
		},
	});
	

	const handleRowClick = useRowSelectionHandler({
		dataList: gridData,
		idKey: 'valueIndex',
		selectedList: selectedWMS1030_R01LotList,
		deletedList: deletedWMS1030_R01LotList,
		addSelected: addSelectedWMS1030_R01LotList,
		removeSelected: removeSelectedWMS1030_R01LotList,
		clickable, setClickable
	});


	// 범용 이벤트 훅 사용 (코드 검색 없이) - 방향키 이동 기능 유지
	/**
	 * 기본 상세 테이블 이벤트 핸들러 훅
	 * 
	 * 주요 기능:
	 * - 키다운 네비게이션 (방향키 이동)
	 * - 셀 클릭/키다운 이벤트 처리
	 * 
	 * @param detailList - 상세 데이터 목록
	 * @param editableColumns - 편집 가능한 컬럼 목록
	 */
	const cellEventHandlers = useDetailTableEvents({
		detailList: gridData as any,
		editableColumns: defaultEditableColumns,
		keyDownConfig: {
			enableArrowNavigation: true,
		}
	});
	
	const cellClickActions: CustomCellClickAction<WMS1030_R01LotDto>[] = useMemo(() => [
		{
			validate: (field, data) => {
				return commonInputColumns.includes(field);
			},
			//aggrid 기본 편집 기능
			action: (e) => { e.api.startEditingCell({ rowIndex: e.rowIndex!, colKey: e.colDef.field! }); }
		},
	], []);
	const handleCellClick = CreateCellClickHandler(gridData, 'valueIndex', cellClickActions);

	//더블클릭 액션 없음
	const cellDoubleClickActions: CustomCellDoubleClickAction<WMS1030_R01LotDto>[] = useMemo(() => [
	], [editWMS1030_R01?.warehouseCd]);
	const handleCellDoubleClick = CreateCellDoubleClickHandler(gridData, 'valueIndex', cellDoubleClickActions);


	const handlePostSort = useCallback((params: PostSortRowsParams<WMS1030_R01LotDto>) => {
		postSortHandler(params, gridData, originalWMS1030_R01LotList, setWMS1030_R01LotList);
	}, [gridData, originalWMS1030_R01LotList, setWMS1030_R01LotList]);

	const pinnedBottomRowData = useMemo(() => {
		return [{
			lotNo: gridData.length.toLocaleString() + "건",
			inputOutQty: gridData.reduce((sum, item) => sum + (deletedWMS1030_R01LotList.includes(item.valueIndex.toString()) ? 0 : Number(item.inputOutQty) || 0), 0),
		}];	  
	}, [gridData, deletedWMS1030_R01LotList,]);

	return (
		<>
			<GridContextWrapper<WMS1030_R01LotDto>
				enableCellCalculation
				gridWrapperRef={gridWrapperRef}
				gridRef={lotGridRef}
				paddedGridData={paddedGridData}
				setPaddedGridData={setWMS1030_R01LotList}
				initSelected={initSelectedWMS1030_R01LotList}
				defaultRowData={defaultWMS1030_R01LotRow}
				baseColumnDefs={lotColumnState ? columnDefs : baseColumnDefs}
				defaultColumnState={defaultColumnState}	
				setColumnState={setLotColumnState}
				localStorageKey={COLUMN_STATE_KEY}
				menuItems={undefined}
			>
				<Grid_Excel<WMS1030_R01LotDto>
					ref={lotGridRef}
					rowData={paddedGridData}	getRowId={getRowId} // +- 버튼 테이블 getRowId는 valueIndex로 고정
					columnDefs={columnDefs}		
					defaultColDef={cellCssDefaultColDef}

					rowClassRules={createRowClassRules<WMS1030_R01LotDto>({
						idKey: 'valueIndex',
						selectedList: selectedWMS1030_R01LotList,
						deletedList: deletedWMS1030_R01LotList
					})}

					onRowClicked={handleRowClick}
					onCellClicked={handleCellClick}
					onCellDoubleClicked={handleCellDoubleClick}
					onCellKeyDown={cellEventHandlers.handleCellKeyDown}
					onCellValueChanged={handleCellChange}
					pinnedBottomRowData={pinnedBottomRowData}
					

					onPostSortRows={handlePostSort} //onSortChanged 삭제, 커스텀 정렬 기능추가 
					// Grid_Select에 공통 로직 처리를 위한 props 전달
					columnStateKey={COLUMN_STATE_KEY}			//컬럼 상태 키(로컬스토리지)	
					setColumnState={setLotColumnState}		//컬럼 상태 저장
					getDefaultColumnState={() => defaultColumnState}
					scrollTop={lotScrollTop}
					setScrollTop={setLotScrollTop}
				/>
			</GridContextWrapper>
		</>
	)
}	
